import React, { useCallback, useEffect, useState, useMemo, useRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import Accordion from '_/views/finance/components/Accordion'
import { useFormContext, useFieldArray, get, useWatch } from 'react-hook-form'
import { useDebounce } from 'use-debounce'
import Autocomplete from '_components/inputs/Autocomplete'
import { BasicInput, MaskBasicInput, Select } from '_/components/inputs'
import { getCitiesOptions, getStatesOptions } from '_/modules/utils/selectors'
import useCepAutoComplete from '_/hooks/use-cep-autocomplete'
import { Button, Datagrid } from '@refera/ui-web'
import { Trash } from '@refera/ui-icons'
import { Link, Divider, Collapse, InputLabel, Tooltip } from '@material-ui/core'
import HelpOutlineOutlinedIcon from '@material-ui/icons/HelpOutlineOutlined'
import ArrowForwardIosIcon from '@material-ui/icons/ArrowForwardIos'
import { getAgenciesSlugs } from '_/modules/form/actions'
import { getAgency } from '_/modules/agency/actions'
import { agenciesSlugsSelector } from '_/modules/form/selectors'
import { getInspectionTradesmenSelector } from '_modules/inspectionBudget/selector'
import { NumericInput } from '_/views/budget-view-v2/components/Inputs/NumericInput/index'

import IconButton from '_/components/svg/icon-button'
import { useDialog } from '_/hooks/use-dialog'

import {
  TOTAL_VALUES,
  percentageToValueMap,
  valueToPercentageMap,
  BUDGET_COLUMNS,
  MENU_PROPS,
  DISCOUNT_NAMES_SET,
  BUDGET_URL,
} from '../../utils/constants'
import { useInspectionLoading } from '../../hooks/use-inspection-loading'
import { useLaborValueUpdate } from '../../hooks/use-labor-value-update'
import { useExecutionDueUpdate } from '../../hooks/use-execution-due-update'
import { useServiceLaborUpdate } from '../../hooks/use-service-labor-update'
import { ExpandCollapseButton } from '../Buttons'
import { ResolutionsTable } from '../resolutions-table'
import {
  getBudgetTotals,
  filterInspectionTradesmenByCity,
  handleDownloadPdf,
  getInspectionBudgetPdfExportMode,
  PDF_EXPORT_MODES,
  normalizeText,
  percentageToValue,
  toNumeric,
  validateSqmOnRange,
} from '_/views/inspection-budget/utils/functions'
import { round } from '_/views/finance/utils/functions'
import Theme from '@refera/ui-core'

import useStyles from './styles'
import { useToast } from '_/hooks/use-toast'
import { datagridStyle } from '_views/inspection-budget/styles'
import { PdfExportModeModal } from '../modals/pdf-export-mode-modal'
import FooterContent from '../FooterContent'

export function GeneralInfoAccordion() {
  const styles = useStyles()
  const dispatch = useDispatch()

  const agenciesSlugs = useSelector(state => agenciesSlugsSelector(state, 'inspection'))
  const tradesmen = useSelector(getInspectionTradesmenSelector)
  const citiesOptions = useSelector(getCitiesOptions)
  const { budget, budgetType, isEditing } = useInspectionLoading()
  const { showToast } = useToast()
  const { showDialog, closeDialog } = useDialog()

  const {
    setValue,
    getValues,
    clearErrors,
    setError,
    formState: { errors },
  } = useFormContext()

  const watchedState = useWatch({ name: 'state' })
  const watchedCity = useWatch({ name: 'city' })
  const watchedTradesman = useWatch({ name: 'tradesman' })
  const watchedSquareFootage = useWatch({ name: 'squareFootage' })
  const lastProcessedSquareFootageRef = useRef()

  const handleAgencies = useCallback(() => {
    const params = {
      handles_inspection: true,
      is_active: true,
      ordering: 'name',
    }

    dispatch(getAgenciesSlugs(params, 'inspection'))
  }, [dispatch])

  useEffect(() => {
    if (!agenciesSlugs?.size) handleAgencies()
  }, [agenciesSlugs?.size, handleAgencies])

  const filteredTradesmen = useMemo(
    () =>
      filterInspectionTradesmenByCity({
        tradesmen: tradesmen?.results || [],
        city: watchedCity,
        state: watchedState,
        citiesOptions,
      }),
    [citiesOptions, tradesmen?.results, watchedCity, watchedState]
  )

  const isTradesmenCatalogLoading = !tradesmen || !citiesOptions?.length
  const isTradesmanSelectionBlocked = !watchedCity || isTradesmenCatalogLoading

  useEffect(() => {
    if (
      !isEditing ||
      isTradesmenCatalogLoading ||
      !watchedCity ||
      !filteredTradesmen.length ||
      getValues('tradesman') ||
      budget?.isOriginal
    ) {
      return
    }

    const referaTradesman = filteredTradesmen.find(t => t.name === 'Refera')
    if (referaTradesman) {
      setValue('tradesman', referaTradesman, { shouldValidate: true, shouldDirty: false })
    }
  }, [
    budget?.isOriginal,
    filteredTradesmen,
    getValues,
    isEditing,
    isTradesmenCatalogLoading,
    setValue,
    watchedCity,
  ])

  useEffect(() => {
    if (!isEditing || budget?.isOriginal || !watchedTradesman || isTradesmenCatalogLoading) return

    if (!watchedCity) {
      setValue('tradesman', null, { shouldValidate: true, shouldDirty: false })
      return
    }

    const currentTradesmanIsAvailable = filteredTradesmen.some(
      tradesman => tradesman?.id === watchedTradesman?.id
    )

    if (!currentTradesmanIsAvailable) {
      setValue('tradesman', null, { shouldValidate: true, shouldDirty: false })
    }
  }, [
    budget?.isOriginal,
    filteredTradesmen,
    isEditing,
    isTradesmenCatalogLoading,
    setValue,
    watchedCity,
    watchedTradesman,
  ])

  const handleBlockedTradesmanClick = useCallback(
    event => {
      if (!isEditing || budget?.isOriginal || !isTradesmanSelectionBlocked) return

      event.preventDefault()
      event.stopPropagation()
      showToast({
        type: 'warning',
        message: isTradesmenCatalogLoading
          ? 'Aguarde o carregamento de cidades e prestadores para escolher um prestador.'
          : 'É necessário selecionar o endereço do imóvel antes de escolher um prestador.',
      })
    },
    [
      budget?.isOriginal,
      isEditing,
      isTradesmanSelectionBlocked,
      isTradesmenCatalogLoading,
      showToast,
    ]
  )

  const updatePercentages = useCallback(
    agency => {
      if (!agency) return

      const inspectionPercentages = agency?.inspectionPercentages || []

      const percentualRefera = inspectionPercentages[0]?.referaPercentage || 0
      const percentualAgency = inspectionPercentages[0]?.agencyPercentage || 0

      const totalProviderWithDiscount = getValues('totalProviderWithDiscount') || 0

      setValue('percentualPriceRefera', percentualRefera, { shouldDirty: false })
      setValue('percentualPriceAgency', percentualAgency, { shouldDirty: false })

      const priceReferaToValue = percentageToValue(percentualRefera, totalProviderWithDiscount)
      const priceAgencyToValue = percentageToValue(percentualAgency, totalProviderWithDiscount)

      setValue('priceRefera', round(priceReferaToValue, 2), { shouldDirty: false })
      setValue('priceAgency', round(priceAgencyToValue, 2), { shouldDirty: false })
    },
    [getValues, setValue]
  )

  const confirmDialog = useCallback(() => {
    return new Promise(resolve => {
      showDialog({
        type: 'warning',
        subject: 'Deseja recalcular os valores?',
        description:
          'Ao recalcular, a alteração da metragem irá impactar o tempo de mão de obra de alguns serviços.',
        labelApprove: 'Recalcular',
        labelCancel: 'Cancelar',
        onApprove: () => {
          closeDialog()
          resolve(true)
        },
        onCancel: () => {
          closeDialog()
          resolve(false)
        },
      })
    })
  }, [showDialog, closeDialog])

  const { getAndUpdateLaborValue } = useLaborValueUpdate({ getValues, setValue, budgetType })
  const { updateExecutionDue } = useExecutionDueUpdate({ getValues, setValue })

  const { handleServiceDataUpdate } = useServiceLaborUpdate({
    getValues,
    setValue,
    showToast,
    getAndUpdateLaborValue,
    updateExecutionDue,
  })

  useEffect(() => {
    if (lastProcessedSquareFootageRef.current == null) {
      lastProcessedSquareFootageRef.current = watchedSquareFootage
    }
  }, [watchedSquareFootage])

  const handleTimeExecutionUpdates = useCallback(async () => {
    const squareMeter = getValues('squareFootage')
    const hasRealChange = squareMeter !== lastProcessedSquareFootageRef.current

    if (!hasRealChange) return

    const compartments = getValues('inspectionBudgetCompartments') ?? []
    const servicesUsingSqm = compartments.flatMap((compartment, cIndex) =>
      (compartment?.inspectionBudgetResolutions || [])
        .map((resolution, rIndex) => ({
          resolution,
          originalIndex: rIndex,
        }))
        .filter(item => item.resolution?.service?.isExecutionTimePerSqm)
        .map(({ resolution, originalIndex }) => ({
          fieldName: `inspectionBudgetCompartments.${cIndex}.inspectionBudgetResolutions.${originalIndex}`,
          value: resolution?.service,
          resolutionIndex: originalIndex,
          compartmentIndex: cIndex,
        }))
    )

    if (servicesUsingSqm?.length) {
      const validSquareMeter = Number.isFinite(Number(squareMeter)) && Number(squareMeter) !== 0
      if (!validSquareMeter) {
        setValue('squareFootage', lastProcessedSquareFootageRef.current, { shouldDirty: true })
        showToast({
          type: 'error',
          message:
            'Metragem inválida. Alguns serviços dependem desse valor, por isso o valor anterior foi mantido.',
        })
        return
      }

      const invalidService = validateSqmOnRange(
        squareMeter,
        servicesUsingSqm.map(s => s.value)
      )

      if (invalidService) {
        setValue('squareFootage', lastProcessedSquareFootageRef.current, { shouldDirty: true })
        showToast({
          type: 'error',
          message: `Não há um range configurado para ${squareMeter} m² no Tipo de MO ${invalidService}. Entre em contato com o administrador para realizar o ajuste.`,
        })
        return
      }
      const approved = await confirmDialog()

      if (!approved) {
        setValue('squareFootage', lastProcessedSquareFootageRef.current, { shouldDirty: true })
        return
      }

      servicesUsingSqm.forEach(item => {
        handleServiceDataUpdate(
          item.fieldName,
          item.value,
          item.compartmentIndex,
          item.resolutionIndex,
          false
        )
      })
    }
    lastProcessedSquareFootageRef.current = squareMeter
  }, [confirmDialog, getValues, handleServiceDataUpdate, setValue, showToast])

  const updateExecutionDueTotal = useCallback(() => {
    const executionDue = getValues('executionDue') ?? 0
    const additionalExecutionDue = getValues('additionalExecutionDue') ?? 0

    const executionDueTotal = executionDue + additionalExecutionDue
    setValue('executionDueTotal', executionDueTotal)
  }, [getValues, setValue])

  const validateAdditionalExecutionDue = useCallback(
    values => {
      if (values.floatValue === undefined) {
        clearErrors('additionalExecutionDue')
        return true
      }

      const executionDue = Number(getValues('executionDue')) || 0
      const isValid = values.floatValue >= -executionDue

      if (isValid) {
        clearErrors('additionalExecutionDue')
      } else {
        setError('additionalExecutionDue', {
          type: 'manual',
          message: 'O tempo de execução total não pode ser menor que 0.',
        })
      }

      return isValid
    },
    [getValues, setError, clearErrors]
  )
  const isReadOnly = !isEditing || budget?.isOriginal

  return (
    <Accordion id="generalInfo" title="Informações Gerais">
      <div className={styles.content}>
        <NumericInput
          required
          name="bitrixId"
          label="ID do bitrix"
          placeholder="0"
          decimalScale={0}
          hasError={errors?.bitrixId?.message}
          disabled={isReadOnly}
          variant="standard"
        />

        <BasicInput
          name="propertyCode"
          label="Código do imóvel"
          required
          placeholder="Ex: AB-00123"
          disabled={isReadOnly}
          labelClasses={styles.label}
          containerClasses={`${styles.inputCompact} ${styles.propertyCodeField}`}
        />
        {budgetType === 'insurance' && (
          <BasicInput
            label={
              <span className={styles.tooltipLabel}>
                Imobiliária
                <Tooltip title="Cliente da garantidora." arrow>
                  <HelpOutlineOutlinedIcon className={styles.tooltipIcon} />
                </Tooltip>
              </span>
            }
            name="clientAgency"
            required
            disabled={isReadOnly}
            labelClasses={styles.label}
            containerClasses={`${styles.inputCompact} ${styles.propertyCodeField}`}
          />
        )}

        <Autocomplete
          name="agency"
          label="Intermediária"
          required
          options={agenciesSlugs?.toJS() || []}
          getOptionLabel={option => option.name || ''}
          getOptionSelected={(option, value) => option.id === value?.id}
          multiple={false}
          autocompleteClasses={styles.compactAutocomplete}
          disabled={isReadOnly}
          customOnChange={(e, agency) => updatePercentages(agency)}
        />
        {budgetType === 'insurance' && (
          <BasicInput
            label="Código de Contrato"
            name="contractCode"
            disabled={isReadOnly}
            labelClasses={styles.label}
            containerClasses={`${styles.inputCompact} ${styles.propertyCodeField}`}
          />
        )}

        {budgetType === 'inspection' && (
          <div className={styles.fieldOverlayWrapper}>
            <Autocomplete
              name="tradesman"
              label="Prestador"
              required
              options={filteredTradesmen}
              getOptionLabel={option => option.name || ''}
              getOptionSelected={(option, value) => option.id === value?.id}
              multiple={false}
              loading={isTradesmenCatalogLoading}
              autocompleteClasses={styles.compactAutocomplete}
              disabled={isReadOnly || isTradesmanSelectionBlocked}
            />
            {!isReadOnly && isTradesmanSelectionBlocked && (
              <div
                role="presentation"
                className={styles.clickBlockOverlay}
                onMouseDown={handleBlockedTradesmanClick}
                onClick={handleBlockedTradesmanClick}
              />
            )}
          </div>
        )}

        <NumericInput
          name="squareFootage"
          label="Metragem quadrada"
          placeholder="0,00"
          prefix=""
          decimalScale={2}
          variant="standard"
          hasError={errors?.squareFootage?.message}
          disabled={isReadOnly}
          onBlur={handleTimeExecutionUpdates}
        />

        <NumericInput
          label={
            <span className={styles.tooltipLabel}>
              Tempo de execução (em dias)
              <Tooltip
                title="A regra segue a soma dos períodos dos tipos de MO, excluindo cortesia/agrupados, acrescida de 1 dia para retirada de chaves e arredondada pra cima, caso tenha algum tipo de MO que fique em período."
                arrow
              >
                <HelpOutlineOutlinedIcon className={styles.tooltipIcon} />
              </Tooltip>
            </span>
          }
          name="executionDue"
          placeholder="0"
          decimalScale={0}
          hasError={errors?.executionDue?.message}
          disabled
          variant="standard"
        />
        <NumericInput
          label="Tempo de execução adicional"
          name="additionalExecutionDue"
          placeholder="0"
          decimalScale={0}
          hasError={errors?.additionalExecutionDue?.message}
          isAllowed={validateAdditionalExecutionDue}
          variant="standard"
          disabled={isReadOnly}
          onBlur={updateExecutionDueTotal}
          allowNegative
        />
        <NumericInput
          label="Tempo de execução total"
          name="executionDueTotal"
          placeholder="0"
          decimalScale={0}
          hasError={errors?.executionDueTotal?.message}
          disabled
          variant="standard"
        />
        <div className={styles.linkWrapper}>
          <Link
            underline="hover"
            classes={{ root: styles.link }}
            target="_blank"
            rel="noopener"
            href={`${BUDGET_URL[budgetType]}/${budget?.budgetParent}`}
            style={{
              visibility: budget?.budgetParent ? 'visible' : 'hidden',
            }}
          >
            Orçamento original
          </Link>
        </div>
      </div>
    </Accordion>
  )
}

export function AddressAccordion() {
  const styles = useStyles()
  const { budget, isEditing } = useInspectionLoading()
  const citiesOptions = useSelector(getCitiesOptions)
  const stateOptions = useSelector(getStatesOptions)
  const tradesmen = useSelector(getInspectionTradesmenSelector)

  const {
    setValue,
    formState: { errors },
  } = useFormContext()

  const watchedState = useWatch({ name: 'state' })
  const watchedCep = useWatch({ name: 'cep' })
  const isReadOnly = !isEditing || budget?.isOriginal
  const isCepCatalogLoading = !citiesOptions?.length || !stateOptions?.length || !tradesmen
  const cepData = useCepAutoComplete(watchedCep, !isReadOnly && !isCepCatalogLoading)

  const cityOptions = useMemo(() => {
    if (!watchedState) return []
    return citiesOptions.filter(c => c.state.uf === watchedState)
  }, [citiesOptions, watchedState])

  useEffect(() => {
    if (cepData?.state) {
      setValue('state', cepData.state)
    }
    if (cepData?.address !== undefined) {
      setValue('address', cepData.address)
    }
    if (cepData?.neighborhood !== undefined) {
      setValue('neighborhood', cepData.neighborhood)
    }
  }, [cepData, setValue])

  useEffect(() => {
    if (cepData?.city && cityOptions.length > 0) {
      const normalizedCepCity = normalizeText(cepData.city)
      const findCity = cityOptions.find(city => normalizeText(city?.label) === normalizedCepCity)

      if (findCity) {
        setValue('city', findCity.label)
      }
    }
  }, [cepData, cityOptions, setValue])
  return (
    <Accordion id="address" title="Endereço">
      <div className={`${styles.content} ${styles.addressContent}`}>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <MaskBasicInput
            label="CEP"
            name="cep"
            mask="99999-999"
            disabled={isReadOnly || isCepCatalogLoading}
            labelClasses={styles.label}
            containerClasses={styles.inputCompact}
            showErrorMessage={!!errors.cep?.message}
          />
          <Link
            href="https://buscacepinter.correios.com.br/app/endereco/index.php?t"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              fontSize: '12px',
              display: 'flex',
              alignItems: 'center',
              marginTop: '4px',
            }}
          >
            Não sei meu CEP <ArrowForwardIosIcon style={{ fontSize: '12px', marginLeft: '4px' }} />
          </Link>
        </div>

        <BasicInput
          label="Endereço"
          name="address"
          required
          disabled={isReadOnly}
          labelClasses={styles.label}
          containerClasses={styles.inputCompact}
          rules={{
            maxLength: {
              value: 255,
              message: `Esse campo aceita no máximo 255 caracteres`,
            },
            required: { value: true, message: 'Esse campo é obrigatório' },
          }}
        />

        <BasicInput
          label="Número"
          name="number"
          required
          disabled={isReadOnly}
          labelClasses={styles.label}
          containerClasses={styles.inputCompact}
          rules={{
            maxLength: {
              value: 255,
              message: `Esse campo aceita no máximo 255 caracteres`,
            },
            required: { value: true, message: 'Esse campo é obrigatório' },
          }}
        />

        <BasicInput
          label="Complemento"
          name="complement"
          disabled={isReadOnly}
          containerClasses={styles.inputCompact}
          labelClasses={styles.label}
          rules={{
            maxLength: {
              value: 255,
              message: `Esse campo aceita no máximo 255 caracteres`,
            },
          }}
        />

        <Select
          label="Estado"
          name="state"
          required
          error={!!errors?.state}
          options={stateOptions}
          disabled={isReadOnly}
          labelClasses={styles.selectLabel}
          getValue={item => item.value}
          getLabel={item => item.label}
          getKey={item => item.value}
          menuProps={MENU_PROPS}
        />

        <Select
          label="Cidade"
          name="city"
          required
          error={!!errors?.city}
          options={cityOptions}
          disabled={isReadOnly}
          labelClasses={styles.selectLabel}
          getValue={item => item.label}
          getLabel={item => item.label}
          getKey={item => item.value}
          menuProps={MENU_PROPS}
        />

        <BasicInput
          label="Bairro"
          name="neighborhood"
          required
          disabled={isReadOnly}
          containerClasses={styles.inputCompact}
          labelClasses={styles.label}
          rules={{
            maxLength: {
              value: 255,
              message: `Esse campo aceita no máximo 255 caracteres`,
            },
            required: { value: true, message: 'Esse campo é obrigatório' },
          }}
        />
      </div>
    </Accordion>
  )
}

const PriceInput = React.memo(({ ...props }) => {
  return <NumericInput {...props} />
})

const ValuesTotalsSection = React.memo(function ValuesTotalsSection({ styles }) {
  const { control, register, setValue, getValues } = useFormContext()
  const { showToast } = useToast()
  const { budget, isEditing, budgetType } = useInspectionLoading()
  const isReadOnly = !isEditing || budget?.isOriginal

  const watchedCompartments =
    useWatch({
      control,
      name: 'inspectionBudgetCompartments',
    }) ?? []
  const [debouncedCompartments] = useDebounce(watchedCompartments, 150)
  const priceRefera = useWatch({ control, name: 'priceRefera' }) ?? 0
  const priceAgency = useWatch({ control, name: 'priceAgency' }) ?? 0
  const discount = useWatch({ control, name: 'discount' }) ?? 0
  const percentualPriceAgency = useWatch({ control, name: 'percentualPriceAgency' }) ?? 0
  const percentualPriceRefera = useWatch({ control, name: 'percentualPriceRefera' }) ?? 0
  const lastDiscount = useRef()

  const { materialsTotal, laborTotal, laborMaxTotal, agencyValuesTotal } = useMemo(() => {
    return getBudgetTotals(debouncedCompartments)
  }, [debouncedCompartments])

  const totalPriceServiceProvider = materialsTotal + laborTotal
  const maxTotalServiceProvider = materialsTotal + laborMaxTotal
  const rates = Number(priceRefera) + Number(priceAgency) - Number(discount)
  const liquidValue = totalPriceServiceProvider + rates
  const maxLiquidValue = maxTotalServiceProvider + rates
  const providerValueNet = totalPriceServiceProvider - Number(discount)
  const previousTotalPriceServiceProviderRef = useRef(providerValueNet)

  const setValueIfChanged = useCallback(
    (fieldName, nextValue, options = { shouldDirty: false }) => {
      const currentValue = getValues(fieldName)
      if (String(currentValue ?? '') === String(nextValue ?? '')) return
      setValue(fieldName, nextValue, options)
    },
    [getValues, setValue]
  )

  const syncPercentageAndValue = useCallback(
    (name, validValue, totalValue) => {
      if (percentageToValueMap[name]) {
        const valueField = percentageToValueMap[name]
        const calculatedValue = (validValue / 100) * totalValue

        setValueIfChanged(valueField, round(calculatedValue, 2))
        setValueIfChanged(name, round(validValue, 4))
      }

      if (valueToPercentageMap[name]) {
        const percentageField = valueToPercentageMap[name]
        const calculatedPercentage = (validValue / totalValue) * 100

        setValueIfChanged(name, round(validValue, 2))
        setValueIfChanged(percentageField, round(calculatedPercentage, 4))
      }
    },
    [setValueIfChanged]
  )

  useEffect(() => {
    register('liquidValue')
    register('maxLiquidValue')
    register('agencyValuesTotal')
  }, [register])

  const handleChangeValues = useCallback(
    name => {
      const value = getValues(name)
      const validValue = value ?? 0
      const currentDiscount = getValues('discount') ?? 0

      if (!totalPriceServiceProvider) return

      let totalProviderWithDiscount = totalPriceServiceProvider

      if (DISCOUNT_NAMES_SET.has(name)) {
        syncPercentageAndValue(name, validValue, totalPriceServiceProvider)
        return
      }

      totalProviderWithDiscount -= currentDiscount
      syncPercentageAndValue(name, validValue, totalProviderWithDiscount)
    },
    [getValues, totalPriceServiceProvider, syncPercentageAndValue]
  )

  const updateTotals = useCallback(() => {
    setValueIfChanged('materialsTotal', materialsTotal, { shouldDirty: false })
    setValueIfChanged('laborValueTotal', laborTotal, { shouldDirty: false })
    setValueIfChanged('liquidValue', Number(liquidValue).toFixed(2), { shouldDirty: false })
    setValueIfChanged('maxLiquidValue', Number(maxLiquidValue).toFixed(2), { shouldDirty: false })
    setValueIfChanged('agencyValuesTotal', agencyValuesTotal, { shouldDirty: false })
    setValueIfChanged('totalPriceServiceProvider', Number(totalPriceServiceProvider).toFixed(2), {
      shouldDirty: false,
    })
  }, [
    agencyValuesTotal,
    materialsTotal,
    laborTotal,
    liquidValue,
    maxLiquidValue,
    totalPriceServiceProvider,
    setValueIfChanged,
  ])

  useEffect(() => {
    updateTotals()
  }, [updateTotals])

  useEffect(() => {
    if (previousTotalPriceServiceProviderRef.current === providerValueNet) return

    previousTotalPriceServiceProviderRef.current = providerValueNet

    const priceReferaToValue = percentageToValue(percentualPriceRefera, providerValueNet)
    const priceAgencyToValue = percentageToValue(percentualPriceAgency, providerValueNet)

    setValueIfChanged('priceRefera', round(priceReferaToValue, 2), { shouldDirty: false })
    setValueIfChanged('priceAgency', round(priceAgencyToValue, 2), { shouldDirty: false })
    setValueIfChanged('totalProviderWithDiscount', providerValueNet, { shouldDirty: false })
  }, [providerValueNet, percentualPriceRefera, percentualPriceAgency, setValueIfChanged])

  useEffect(() => {
    if (lastDiscount.current === undefined) {
      lastDiscount.current = discount
    }
  }, [discount])

  useEffect(() => {
    const discountPercentage = getValues('percentualDiscount') || 0
    const calculatedValue = (discountPercentage / 100) * totalPriceServiceProvider
    setValueIfChanged('discount', round(calculatedValue, 2))
    lastDiscount.current = calculatedValue
  }, [getValues, setValueIfChanged, totalPriceServiceProvider])

  const handleReloadPreviousDiscount = useCallback(
    (name, value) => {
      const numeric = toNumeric(value)

      const monetary =
        name === 'percentualDiscount' ? (numeric / 100) * totalPriceServiceProvider : numeric

      if (monetary > totalPriceServiceProvider) {
        const percentage =
          totalPriceServiceProvider > 0
            ? (lastDiscount.current / totalPriceServiceProvider) * 100
            : 0
        showToast({
          type: 'error',
          message: 'O desconto não pode ser maior que o total do prestador',
        })

        setValue('discount', lastDiscount.current)
        setValue('percentualDiscount', percentage)

        return
      }

      lastDiscount.current = monetary
    },
    [totalPriceServiceProvider, showToast, setValue]
  )

  const handleBlur = useCallback(
    e => {
      const { name, value } = e.target

      if (!value) {
        setValue(name, 0)
        return
      }

      if (!DISCOUNT_NAMES_SET.has(name)) return

      handleReloadPreviousDiscount(name, value)
    },
    [setValue, handleReloadPreviousDiscount]
  )

  return (
    <div className={styles.rowTotals}>
      {TOTAL_VALUES(styles, budgetType)?.map(({ name, label, style, ...rest }) => (
        <div key={name} style={style}>
          <PriceInput
            name={name}
            label={label}
            disabled={isReadOnly}
            decimalScale={2}
            onBlur={handleBlur}
            onChange={() => handleChangeValues(name)}
            {...rest}
          />
        </div>
      ))}
    </div>
  )
})

const ValuesFooterSummary = React.memo(function ValuesFooterSummary() {
  const { control } = useFormContext()
  const { budgetType } = useInspectionLoading()

  const liquidValue = useWatch({ control, name: 'liquidValue' }) ?? 0
  const maxLiquidValue = useWatch({ control, name: 'maxLiquidValue' }) ?? 0
  const agencyValuesTotal = useWatch({ control, name: 'agencyValuesTotal' }) ?? 0

  return (
    <FooterContent
      budgetType={budgetType}
      liquidValue={Number(liquidValue) || 0}
      maxLiquidValue={Number(maxLiquidValue) || 0}
      agencyValuesTotal={Number(agencyValuesTotal) || 0}
    />
  )
})

export function ValuesAccordion() {
  const styles = useStyles()
  const {
    control,
    getValues,
    setValue,
    formState: { errors },
  } = useFormContext()

  const { closeDialog, showDialog } = useDialog()
  const [openCompartments, setOpenCompartments] = useState({})
  const { budget, isEditing, budgetType } = useInspectionLoading()
  const isReadOnly = !isEditing || budget?.isOriginal
  const { getAndUpdateLaborValue } = useLaborValueUpdate({ getValues, setValue, budgetType })
  const { updateExecutionDue } = useExecutionDueUpdate({ getValues, setValue })

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'inspectionBudgetCompartments',
    keyName: 'fieldId',
  })

  const getCompartmentDefaultValue = useCallback(
    index => {
      const path = `inspectionBudgetCompartments.${index}.compartment`
      return getValues(path) ?? fields[index]?.compartment ?? ''
    },
    [fields, getValues]
  )

  const toggleCompartment = compartmentId => {
    setOpenCompartments(prev => ({
      ...prev,
      [compartmentId]: !(prev[compartmentId] ?? true),
    }))
  }

  useEffect(() => {
    setOpenCompartments(prev => {
      const next = {}

      fields.forEach(field => {
        next[field.fieldId] = prev[field.fieldId] ?? true
      })

      return next
    })
  }, [fields])

  const handleRemove = useCallback(
    index => {
      showDialog({
        type: 'warning',
        subject: 'Você tem certeza que deseja excluir esse registro?',
        labelApprove: 'Confirmar',
        labelCancel: 'Cancelar',
        onCancel: closeDialog,
        onApprove: () => {
          remove(index)
          Promise.resolve().then(() => {
            getAndUpdateLaborValue()
            updateExecutionDue()
          })
          closeDialog()
        },
      })
    },
    [showDialog, closeDialog, remove, getAndUpdateLaborValue, updateExecutionDue]
  )

  return (
    <Accordion id="values" title="Problemas e Serviços">
      <div className={styles.wrapper}>
        <div className={styles.valuesContent}>
          {fields.map((field, index) => {
            const open = openCompartments[field.fieldId] ?? true
            const hasError = get(errors, `inspectionBudgetCompartments.${index}.compartment`)

            return (
              <div key={field.fieldId}>
                <div className={styles.compartmentHeader}>
                  <ExpandCollapseButton
                    open={open}
                    onToggle={() => toggleCompartment(field.fieldId)}
                  />

                  <InputLabel
                    variant="standard"
                    error={hasError}
                    className={styles.label}
                    disabled={isReadOnly}
                  >
                    Ambiente<span className={styles.required}>*</span>
                  </InputLabel>
                </div>

                <div className={`${styles.row} ${styles.rowOneColumn}`}>
                  <div className={styles.rowSingle}>
                    <BasicInput
                      name={`inspectionBudgetCompartments.${index}.compartment`}
                      defaultValue={getCompartmentDefaultValue(index)}
                      disabled={isReadOnly}
                      containerClasses={styles.inputCompact}
                      labelClasses={styles.label}
                    />

                    <IconButton
                      variant="ghost"
                      onClick={() => handleRemove(index)}
                      disabled={isReadOnly}
                    >
                      <Trash
                        color={
                          isReadOnly ? Theme.Colors.Grayscale.ThirtyTwo : Theme.Colors.Red.Base
                        }
                        fontSize={20}
                      />
                    </IconButton>
                  </div>
                </div>

                <Collapse in={open}>
                  <ResolutionsTable
                    name={`inspectionBudgetCompartments.${index}.inspectionBudgetResolutions`}
                    compartmentIndex={index}
                  />
                </Collapse>
              </div>
            )
          })}

          <ValuesTotalsSection styles={styles} />
        </div>

        <div className={styles.footer}>
          <Divider className={styles.divider} />
          <div className={styles.footerContent}>
            <Button
              variant="secondary"
              style={{ width: '160px' }}
              onClick={() =>
                append({
                  compartment: null,
                  inspectionBudgetResolutions: [{ service: null }],
                })
              }
              disabled={isReadOnly}
            >
              + Adicionar ambiente
            </Button>
            <ValuesFooterSummary />
          </div>
        </div>
      </div>
    </Accordion>
  )
}

export function RelatedBudgetsAccordion() {
  const dispatch = useDispatch()
  const [loading, setLoading] = useState(false)
  const [isPdfModeModalOpen, setIsPdfModeModalOpen] = useState(false)
  const [defaultPdfMode, setDefaultPdfMode] = useState(PDF_EXPORT_MODES.IDEAL)
  const [showMaximumRecommendation, setShowMaximumRecommendation] = useState(false)
  const [pendingDownloadRow, setPendingDownloadRow] = useState(null)
  const agencyExceptionCacheRef = useRef(new Map())
  const { showToast } = useToast()
  const { relatedBudgets, budgetType } = useInspectionLoading()

  const handleGoToBudget = useCallback(
    event => {
      const { id } = event.row
      window.open(`${BUDGET_URL[budgetType]}/${id}`, '_blank', 'noopener,noreferrer')
    },
    [budgetType]
  )

  const handleDownload = useCallback(
    async (event, row) => {
      event.preventDefault()
      event.stopPropagation()

      if (budgetType === 'insurance') {
        const agencyId = row?.agency?.id
        let cityExceptionList = agencyExceptionCacheRef.current.get(agencyId)

        if (!cityExceptionList && agencyId) {
          try {
            const agencyData = await dispatch(getAgency(agencyId))
            cityExceptionList = agencyData?.insuranceCityExceptionList || []
            agencyExceptionCacheRef.current.set(agencyId, cityExceptionList)
          } catch (error) {
            cityExceptionList = []
          }
        }

        const defaultMode = getInspectionBudgetPdfExportMode({
          city: row?.city,
          state: row?.state,
          cityExceptionList,
        })
        setDefaultPdfMode(defaultMode)
        setShowMaximumRecommendation(defaultMode === PDF_EXPORT_MODES.MAXIMUM)
        setPendingDownloadRow(row)
        setIsPdfModeModalOpen(true)
        return
      }

      await handleDownloadPdf({
        id: row.id,
        dispatch,
        showToast,
        setLoading,
        pdfFileSuffix: row?.pdfFileSuffix,
        pdfReferenceId: row?.pdfReferenceId,
      })
    },
    [budgetType, dispatch, showToast, setLoading]
  )

  const handleConfirmPdfMode = useCallback(
    async selectedMode => {
      if (!pendingDownloadRow?.id) return

      await handleDownloadPdf({
        id: pendingDownloadRow.id,
        dispatch,
        showToast,
        setLoading,
        pdfFileSuffix: pendingDownloadRow?.pdfFileSuffix,
        pdfReferenceId: pendingDownloadRow?.pdfReferenceId,
        exportMode: selectedMode,
      })
      setIsPdfModeModalOpen(false)
      setPendingDownloadRow(null)
    },
    [budgetType, dispatch, pendingDownloadRow, showToast]
  )

  const handlePdfModeModalVisibility = useCallback(isOpen => {
    setIsPdfModeModalOpen(isOpen)
    if (!isOpen) {
      setPendingDownloadRow(null)
    }
  }, [])

  return (
    <Accordion id="relatedBudgets" title="Orçamentos relacionados" defaultExpanded={false}>
      <Datagrid
        autoHeight
        rows={relatedBudgets ?? []}
        rowCount={relatedBudgets?.length ?? 0}
        loading={loading}
        getRowId={row => row?.id}
        columns={BUDGET_COLUMNS(budgetType, loading, handleDownload)}
        sx={datagridStyle}
        onRowClick={handleGoToBudget}
        hideFooter
      />
      <PdfExportModeModal
        openModal={isPdfModeModalOpen}
        setOpenModal={handlePdfModeModalVisibility}
        defaultMode={defaultPdfMode}
        showMaximumRecommendation={showMaximumRecommendation}
        isLoading={loading}
        onConfirm={handleConfirmPdfMode}
      />
    </Accordion>
  )
}
